import React, { memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'reactstrap';
import DynamicAutoComplete from '../../../../DynamicAutoComplete';
import { TOOLTIPS_TEXT, DEVICE_LOCK_BY_SOMEONE } from '../../../../../../lib/constants';
import InfoPopup from '../../../../../InfoPopup';
import { toast } from 'react-toastify';
import { boxConfig, getEpikBoxData, fetchBoxById, updateEpikBox } from '../../../../../../actions/EpikBoxes';
import ConfirmationModal from '../../../../../ConfirmationModal';
import { useUnifiedMutate } from '../../../../../../../src/hooks/useUnifiedMutate.js';
import { ResetBoxToDefault } from './ResetBoxToDefault/index.jsx';
import { publishDeviceSwapStatus } from '../../../../../../lib/deviceSwapSync';
import SpinnerCircles from '../../../../../Spinner/SpinerCircles';
import {
  alignDestinationDatacenter,
  datacentersDiffer,
  destinationDcMismatchMessage,
} from '../../../../../../lib/deviceSwapDc';

const WARNING_TEXT = `This action will migrate all port configurations, options, networking, OOBM, and port features to the new destination device and return this device to factory settings. Integration will be triggered on the destination device. Are you sure you want to proceed?`;

const ExpansionModuleAdminTools = memo(
  ({ serialBoxes, removeValueFromArray, onOptionsChange, selectedBox, lockDeviceHandler }) => {
    const token = localStorage.getItem('jwtToken');
    const dispatch = useDispatch();

    const { box } = useSelector(state => state.AppState.boxDetails);
    const { role } = useSelector(state => state.AppState.user);
    const lockStatusBox = useSelector(state => state.UIState.epikBoxes.lockStatusEpikBox);
    const { loading: resetBoxLoading } = useSelector(
      state => state.UIState.epikBoxes.resetBoxToDefault,
    );

    const [warningStatus, setWarningStatus] = useState(false);
    const [boxLockWarning, setBoxLockWarning] = useState(false);
    const [warningText, setWarningText] = useState(WARNING_TEXT);
    const [boxConfigStatus, setBoxConfigStatus] = useState('');
    const [transferNotes, setTransferNotes] = useState(true);
    const [dcMismatchOpen, setDcMismatchOpen] = useState(false);
    const [dcSwitchLoading, setDcSwitchLoading] = useState(false);
    const [dcMismatch, setDcMismatch] = useState(null);

    const { mutate: swapExpansionModule, isLoading: isBoxSwapping } = useUnifiedMutate();

    const isAdmin = ['superAdmin', 'companyGroupAdmin'].includes(role);
    const isSerialBoxesAvailable = serialBoxes.length > 0;

    const handleExecuteClick = async () => {
      if (lockStatusBox?.locked) {
        toast.info(DEVICE_LOCK_BY_SOMEONE);
        return;
      }

      if (!selectedBox) {
        toast.info(`Please select destination serial.`);
        return;
      }

      if (document.getElementById('test-time-remaining')?.hasAttribute('hidden')) {
        setBoxLockWarning(true);
      } else {
        await executeBoxConfig();
      }
    };

    const executeBoxConfig = async () => {
      const response = await dispatch(boxConfig(token, box?.serialNumber, selectedBox, true));
      setBoxConfigStatus(response);
      setWarningText(!response?.blankConfig ? response?.message : WARNING_TEXT);
      if (response?.allowed) {
        if (
          datacentersDiffer(response.originDatacenter, response.targetDatacenter) &&
          response.targetBoxId
        ) {
          setDcMismatch({
            originDatacenter: response.originDatacenter,
            targetDatacenter: response.targetDatacenter,
            targetBoxId: response.targetBoxId,
          });
          setDcMismatchOpen(true);
          return;
        }
        setWarningStatus(true);
      }
    };

    const onConfirmDestinationDcSwitch = async () => {
      if (!dcMismatch?.targetBoxId || dcSwitchLoading) return;
      setDcSwitchLoading(true);
      try {
        const result = await alignDestinationDatacenter({
          token,
          dispatch,
          destBoxId: dcMismatch.targetBoxId,
          sourceDatacenter: dcMismatch.originDatacenter,
          fetchBoxById,
          updateEpikBox,
        });
        if (!result.ok) {
          toast.error(result.reason || 'Failed to switch the destination data center.');
          return;
        }
        setDcMismatchOpen(false);
        setDcMismatch(null);
        setWarningStatus(true);
      } finally {
        setDcSwitchLoading(false);
      }
    };

    const onProceed = async () => {
      if (isBoxSwapping) {
        return;
      }

      const currBox = box?.serialNumber;
      setWarningStatus(false);

      const endpoint = 'obis/swap-expansion-module';
      const method = 'PUT';

      const startedAt = new Date().toISOString();
      dispatch({
        type: 'SWAP_STATUS_UPDATE',
        swapInProgress: true,
        swapPartnerSerial: selectedBox,
        swapStartedAt: startedAt,
      });
      publishDeviceSwapStatus({
        serials: [currBox, selectedBox],
        swapInProgress: true,
        sourceSerial: currBox,
        destSerial: selectedBox,
        swapPartnerSerialBySerial: {
          [currBox]: selectedBox,
          [selectedBox]: currBox,
        },
        swapStartedAt: startedAt,
      });

      try {
        const result = await swapExpansionModule({
          isMyEpik: false,
          endpoint,
          method,
          body: {
            currentBox: currBox,
            targetBox: selectedBox,
            boxId: box?._id,
            isBoxTab: true,
            transferNotes,
          },
        });

        dispatch({
          type: 'DEVICE_SWAP_RESULT',
          result:
            result && typeof result === 'object'
              ? result
              : {
                  success: false,
                  message: 'Device swap failed',
                  errorResp: [],
                  failedFeatures: [],
                },
        });
      } finally {
        dispatch({
          type: 'SWAP_STATUS_UPDATE',
          swapInProgress: false,
          swapPartnerSerial: null,
          swapStartedAt: null,
        });
        publishDeviceSwapStatus({
          serials: [currBox, selectedBox],
          swapInProgress: false,
          sourceSerial: currBox,
          destSerial: selectedBox,
          swapPartnerSerialBySerial: {},
          swapStartedAt: null,
        });
        if (box?._id) {
          await dispatch(getEpikBoxData(token, box._id));
        }
      }
    };

    return (
      <div className="admin-tools-container">
        {isAdmin && (
          <>
            <h1 className="device heading d-flex">
              Device Swap
              <sup>
                <InfoPopup id="device-swap" text={TOOLTIPS_TEXT.DEVICE_SWAP} />
              </sup>
            </h1>
            <div className="modam-row">
              <div>
                <p className="destination-text">Destination Serial</p>
                <DynamicAutoComplete
                  isDisabled={!isSerialBoxesAvailable || isBoxSwapping || dcSwitchLoading || !!box?.swapInProgress}
                  dropdownList={removeValueFromArray(serialBoxes, box?.serialNumber)}
                  onChange={({ suggestion }) => onOptionsChange(suggestion)}
                  placeholder={isSerialBoxesAvailable ? 'Serial Number' : 'No Boxes Found'}
                />
              </div>
              {isSerialBoxesAvailable && (
                <Button
                  onClick={handleExecuteClick}
                  disabled={!selectedBox || isBoxSwapping || dcSwitchLoading || !!box?.swapInProgress}
                  color="primary"
                  className="admin-tool-execute-btn"
                >
                  {isBoxSwapping || dcSwitchLoading || box?.swapInProgress ? <SpinnerCircles /> : 'Execute'}
                </Button>
              )}
              {!boxConfigStatus?.allowed && (
                <p className="font-weight-bold ml-3 mt-2 config-color">
                  {boxConfigStatus?.message}
                </p>
              )}
            </div>

            {/* Reset to Default */}
            <div style={{ marginTop: '20px' }}>
              <ResetBoxToDefault loading={resetBoxLoading} setDcUpdatePopup={setBoxLockWarning} />
            </div>
          </>
        )}

        {dcMismatchOpen && (
          <ConfirmationModal
            isOpen={dcMismatchOpen}
            onCancel={() => {
              if (dcSwitchLoading) return;
              setDcMismatchOpen(false);
              setDcMismatch(null);
            }}
            headerText="Data Center Switch Required"
            confirmBtnText="Switch Data Center"
            bodyText={
              dcSwitchLoading
                ? 'Switching the destination data center. Please wait. The destination device will come back online before the swap starts.'
                : destinationDcMismatchMessage(
                    dcMismatch?.originDatacenter,
                    dcMismatch?.targetDatacenter
                  )
            }
            onConfirm={onConfirmDestinationDcSwitch}
            loading={dcSwitchLoading}
          />
        )}

        {warningStatus && (
          <ConfirmationModal
            isOpen={warningStatus}
            onCancel={() => setWarningStatus(false)}
            bodyText={warningText}
            onConfirm={onProceed}
            loading={isBoxSwapping}
            extraContent={
              <label className="swap-transfer-notes" htmlFor="expansion-transfer-notes-checkbox">
                <input
                  id="expansion-transfer-notes-checkbox"
                  type="checkbox"
                  checked={transferNotes}
                  onChange={e => setTransferNotes(e.target.checked)}
                />
                Transfer all notes to the destination device
              </label>
            }
          />
        )}

        {boxLockWarning && (
          <ConfirmationModal
            bodyText="You are attempting to make changes to a device that is not currently locked for editing. To prevent conflicting changes, please lock the device before proceeding."
            headerText="Device Lock Required"
            confirmBtnText="Lock & Edit"
            close="No"
            isOpen={boxLockWarning}
            onCancel={() => setBoxLockWarning(false)}
            onConfirm={() => {
              lockDeviceHandler();
              setBoxLockWarning(false);
            }}
          />
        )}
      </div>
    );
  },
);

export default ExpansionModuleAdminTools;
